import { reactive, ref } from 'vue';
import type { CesgOrganisationStakeholder } from '../types';

// --- Internal types ---

export type ProjectOrganisationCesgMode =
  | 'normal'
  | 'edit'
  | 'saving'
  | 'read-only';

export interface CesgStakeholderState extends CesgOrganisationStakeholder {
  isNew: boolean;
}

export interface InternalCesgOrganisation {
  name: string;
  stakeholders: CesgStakeholderState[];
}

// --- Dispatch action union ---

export type ProjectOrganisationCesgDispatchAction =
  | {
      type: 'set-organisation';
      name: string;
      stakeholders: CesgOrganisationStakeholder[];
    }
  | { type: 'set-mode'; mode: ProjectOrganisationCesgMode }
  | { type: 'take-snapshot' }
  | { type: 'restore-snapshot' }
  | {
      type: 'add-from-picker';
      userId: string;
      name: string;
      email: string;
      role: string;
    }
  | { type: 'delete-stakeholder'; id: string }
  | { type: 'move-up'; id: string }
  | { type: 'move-down'; id: string }
  | { type: 'update-stakeholder'; id: string; role: string }
  | {
      type: 'assign-user';
      id: string;
      userId: string;
      name: string;
      email: string;
    };

// --- Factory ---

interface CreateProjectOrganisationCesgStoreInput {
  name: string;
  stakeholders: CesgOrganisationStakeholder[];
  initialMode: ProjectOrganisationCesgMode;
}

export const createProjectOrganisationCesgStore = ({
  name: initialName,
  stakeholders: initialStakeholders,
  initialMode,
}: CreateProjectOrganisationCesgStoreInput) => {
  const mode = ref<ProjectOrganisationCesgMode>(initialMode);

  const organisation = reactive<InternalCesgOrganisation>({
    name: initialName,
    stakeholders: initialStakeholders
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ ...s, isNew: false })),
  });

  let snapshot: CesgStakeholderState[] | null = null;

  const dispatch = (action: ProjectOrganisationCesgDispatchAction): void => {
    switch (action.type) {
      case 'set-organisation': {
        organisation.name = action.name;
        organisation.stakeholders.splice(
          0,
          organisation.stakeholders.length,
          ...action.stakeholders
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((s) => ({ ...s, isNew: false })),
        );
        break;
      }

      case 'set-mode': {
        mode.value = action.mode;
        break;
      }

      case 'take-snapshot': {
        snapshot = organisation.stakeholders.map((s) => ({ ...s }));
        break;
      }

      case 'restore-snapshot': {
        if (snapshot) {
          organisation.stakeholders.splice(
            0,
            organisation.stakeholders.length,
            ...snapshot,
          );
        }
        snapshot = null;
        break;
      }

      case 'add-from-picker': {
        organisation.stakeholders.push({
          id: String(Date.now()),
          order: 0,
          isNew: true,
          userId: action.userId,
          name: action.name,
          email: action.email,
          role: action.role,
        });
        break;
      }

      case 'delete-stakeholder': {
        const idx = organisation.stakeholders.findIndex(
          (s) => s.id === action.id,
        );
        if (idx !== -1) organisation.stakeholders.splice(idx, 1);
        break;
      }

      case 'move-up': {
        const idx = organisation.stakeholders.findIndex(
          (s) => s.id === action.id,
        );
        if (idx > 0) {
          [organisation.stakeholders[idx - 1], organisation.stakeholders[idx]] =
            [
              organisation.stakeholders[idx],
              organisation.stakeholders[idx - 1],
            ];
        }
        break;
      }

      case 'move-down': {
        const idx = organisation.stakeholders.findIndex(
          (s) => s.id === action.id,
        );
        if (idx !== -1 && idx < organisation.stakeholders.length - 1) {
          [organisation.stakeholders[idx], organisation.stakeholders[idx + 1]] =
            [
              organisation.stakeholders[idx + 1],
              organisation.stakeholders[idx],
            ];
        }
        break;
      }

      case 'update-stakeholder': {
        const s = organisation.stakeholders.find((s) => s.id === action.id);
        if (s) s.role = action.role;
        break;
      }

      case 'assign-user': {
        const s = organisation.stakeholders.find((s) => s.id === action.id);
        if (s) {
          s.userId = action.userId;
          s.name = action.name;
          s.email = action.email;
        }
        break;
      }
    }
  };

  return {
    organisation,
    mode,
    get snapshot() {
      return snapshot;
    },
    dispatch,
  };
};
